import * as i0 from '@angular/core';
import { Injectable, Optional, Inject, Directive, Input, Output, Component, ChangeDetectionStrategy, HostListener, Pipe, NgModule } from '@angular/core';
import { from, Subject } from 'rxjs';
import { init, PickerDisplayMode } from 'filestack-js';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
const _c0 = ["*"];
function PickerOverlayComponent_div_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 1);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("id", ctx_r0.elementId);
  }
}
let FilestackService = /*#__PURE__*/(() => {
  class FilestackService {
    constructor(config) {
      this.config = config;
      if (!config) {
        return;
      }
      this.clientOptions = config.options;
      this.apikey = config.apikey;
    }
    get client() {
      if (!this.clientInstance) {
        return this.init();
      }
      return this.clientInstance;
    }
    set client(client) {
      this.clientInstance = client;
    }
    /**
     * Set another client instance as this one used by FilestackService
     * @param client - An instance of filestack client class
     */
    setClientInstance(client) {
      if (client) {
        this.client = client;
      }
    }
    /**
     * Initialize filestack client
     * @param apikey - Filestack apikey
     * @param clientOptions - Client options
     */
    init(apikey, clientOptions) {
      this.client = init(apikey || this.apikey, clientOptions || this.clientOptions);
      return this.client;
    }
    /**
     * Instance of picker class
     * @param options - picker options
     */
    picker(options) {
      return this.client.picker(options);
    }
    /**
     * Generate url with filestack transformations
     * @param url - Filestack handle or external url
     * @param options - Transformation options
     * @param b64 - Encode url params in base64
     */
    transform(url, options, b64) {
      return this.client.transform(url, options, b64);
    }
    /**
     * Access files via their Filestack handles
     * @param handle - Filestack handle
     * @param options - Retrieve options
     * @param security - Filestack security object
     */
    retrieve(handle, options, security) {
      return from(this.client.retrieve(handle, options, security));
    }
    /**
     * Retrieve detailed data of stored files
     * @param handle - Filestack handle
     * @param options - Metadata options
     * @param security - Filestack security object
     */
    metadata(handle, options, security) {
      return from(this.client.metadata(handle, options, security));
    }
    /**
     * Store a file from its URL
     * @param url - Valid url to file
     * @param options - Store params
     * @param token - Optional control token to call .cancel()
     * @param security - Filestack security object
     */
    storeURL(url, options, token, security) {
      return from(this.client.storeURL(url, options, token, security));
    }
    /**
     * Upload a provided file
     * @param file - A file or array of files to upload
     * @param options - Upload options
     * @param storeOptions - Store options
     * @param token - Optional control token to call .cancel()
     * @param security - Filestack security object
     */
    upload(file, options, storeOptions, token, security) {
      if (Array.isArray(file)) {
        return from(this.client.multiupload(file, options, storeOptions, token, security));
      }
      return from(this.client.upload(file, options, storeOptions, token, security));
    }
    /**
     * Remove a file from storage and the Filestack system
     * @param handle - Filestack handle
     * @param security - Filestack security object
     */
    remove(handle, security) {
      return from(this.client.remove(handle, security));
    }
    /**
     * Remove a file only from the Filestack system. The file remains in storage
     * @param handle - Filestack handle
     * @param security - Filestack security object
     */
    removeMetadata(handle, security) {
      return from(this.client.removeMetadata(handle, security));
    }
    /**
     * Used for viewing files via Filestack handles or storage aliases
     * @param handle - Filestack handle
     * @param options - Preview options
     */
    preview(handle, options) {
      return this.client.preview(handle, options);
    }
    /**
     * Clear all current cloud sessions in the picker or
     * particular one if name is passed
     * @param name - Optional cloud name to be passed
     */
    logout(name) {
      return from(this.client.logout(name));
    }
  }
  FilestackService.ɵfac = function FilestackService_Factory(t) {
    return new (t || FilestackService)(i0.ɵɵinject('config', 8));
  };
  FilestackService.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: FilestackService,
    factory: FilestackService.ɵfac
  });
  return FilestackService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let PickerBaseDirective = /*#__PURE__*/(() => {
  class PickerBaseDirective {
    constructor(filestackService) {
      this.filestackService = filestackService;
      this.elementId = 'picker-container';
      this.uploadSuccess = new Subject();
      this.uploadError = new Subject();
      this.generateId();
    }
    ngOnInit() {
      this.filestackService.init(this.apikey, this.clientOptions);
    }
    generateId() {
      this.elementId = `picker-container-${Date.now()}`;
    }
    ngOnDestroy() {
      if (this.picker) {
        this.picker.close();
      }
    }
  }
  PickerBaseDirective.ɵfac = function PickerBaseDirective_Factory(t) {
    return new (t || PickerBaseDirective)(i0.ɵɵdirectiveInject(FilestackService));
  };
  PickerBaseDirective.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: PickerBaseDirective,
    inputs: {
      apikey: "apikey",
      pickerOptions: "pickerOptions",
      clientOptions: "clientOptions",
      file: "file",
      source: "source"
    },
    outputs: {
      uploadSuccess: "uploadSuccess",
      uploadError: "uploadError"
    }
  });
  return PickerBaseDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let PickerOverlayComponent = /*#__PURE__*/(() => {
  class PickerOverlayComponent extends PickerBaseDirective {
    constructor() {
      super(...arguments);
      this.isActive = false;
    }
    ngAfterContentInit() {
      // Overwrite display mode to be always 'overlay' in this component
      this.picker = this.filestackService.picker({
        ...this.pickerOptions,
        rootId: `picker-overlay-${Date.now()}`,
        container: this.elementId,
        displayMode: PickerDisplayMode.overlay,
        onUploadDone: res => this.uploadSuccess.next(res),
        onClose: () => {
          this.isActive = false;
          this.generateId();
        }
      });
      console.log({
        ...this.pickerOptions,
        container: `#${this.elementId}`,
        displayMode: PickerDisplayMode.overlay,
        onUploadDone: res => this.uploadSuccess.next(res),
        onClose: () => {
          this.isActive = false;
          this.generateId();
        }
      });
    }
    onClick(event) {
      if (this.isActive) {
        return;
      }
      event.stopPropagation();
      event.preventDefault();
      this.isActive = true;
      // Picker open success handler there is ommited, because it's accessible from pickerOptions
      this.picker.open().catch(err => this.uploadError.next(err));
    }
  }
  PickerOverlayComponent.ɵfac = /* @__PURE__ */(() => {
    let ɵPickerOverlayComponent_BaseFactory;
    return function PickerOverlayComponent_Factory(t) {
      return (ɵPickerOverlayComponent_BaseFactory || (ɵPickerOverlayComponent_BaseFactory = i0.ɵɵgetInheritedFactory(PickerOverlayComponent)))(t || PickerOverlayComponent);
    };
  })();
  PickerOverlayComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: PickerOverlayComponent,
    selectors: [["ng-picker-overlay"]],
    hostBindings: function PickerOverlayComponent_HostBindings(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵlistener("click", function PickerOverlayComponent_click_HostBindingHandler($event) {
          return ctx.onClick($event);
        });
      }
    },
    features: [i0.ɵɵInheritDefinitionFeature],
    ngContentSelectors: _c0,
    decls: 3,
    vars: 1,
    consts: [[3, "id", 4, "ngIf"], [3, "id"]],
    template: function PickerOverlayComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "div");
        i0.ɵɵprojection(1, 0, [1, "ng-picker"]);
        i0.ɵɵtemplate(2, PickerOverlayComponent_div_2_Template, 1, 1, "div", 0);
        i0.ɵɵelementEnd();
      }
      if (rf & 2) {
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", ctx.isActive);
      }
    },
    dependencies: [i1.NgIf],
    encapsulation: 2,
    changeDetection: 0
  });
  return PickerOverlayComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let PickerDropPaneComponent = /*#__PURE__*/(() => {
  class PickerDropPaneComponent extends PickerBaseDirective {
    ngAfterContentInit() {
      // Overwrite display mode to be always 'dropPane' in this component
      this.picker = this.filestackService.picker({
        ...this.pickerOptions,
        rootId: `picker-overlay-${Date.now()}`,
        displayMode: PickerDisplayMode.dropPane,
        container: this.elementId,
        onUploadDone: res => this.uploadSuccess.next(res)
      });
      // Picker open success handler there is ommited, because it's accessible from pickerOptions
      this.picker.open().then(void 0).catch(err => this.uploadError.next(err));
    }
  }
  PickerDropPaneComponent.ɵfac = /* @__PURE__ */(() => {
    let ɵPickerDropPaneComponent_BaseFactory;
    return function PickerDropPaneComponent_Factory(t) {
      return (ɵPickerDropPaneComponent_BaseFactory || (ɵPickerDropPaneComponent_BaseFactory = i0.ɵɵgetInheritedFactory(PickerDropPaneComponent)))(t || PickerDropPaneComponent);
    };
  })();
  PickerDropPaneComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: PickerDropPaneComponent,
    selectors: [["ng-picker-drop-pane"]],
    features: [i0.ɵɵInheritDefinitionFeature],
    decls: 1,
    vars: 1,
    consts: [[1, "ng-picker", 3, "id"]],
    template: function PickerDropPaneComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵelement(0, "div", 0);
      }
      if (rf & 2) {
        i0.ɵɵproperty("id", ctx.elementId);
      }
    },
    encapsulation: 2,
    changeDetection: 0
  });
  return PickerDropPaneComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let PickerInlineComponent = /*#__PURE__*/(() => {
  class PickerInlineComponent extends PickerBaseDirective {
    ngAfterContentInit() {
      // Overwrite display mode to be always 'inline' in this component
      this.picker = this.filestackService.picker({
        ...this.pickerOptions,
        rootId: `picker-overlay-${Date.now()}`,
        displayMode: PickerDisplayMode.inline,
        container: this.elementId,
        onUploadDone: res => this.uploadSuccess.next(res)
      });
      // Picker open success handler there is ommited, because it's accessible from pickerOptions
      this.picker.open().then(void 0).catch(err => this.uploadError.next(err));
    }
  }
  PickerInlineComponent.ɵfac = /* @__PURE__ */(() => {
    let ɵPickerInlineComponent_BaseFactory;
    return function PickerInlineComponent_Factory(t) {
      return (ɵPickerInlineComponent_BaseFactory || (ɵPickerInlineComponent_BaseFactory = i0.ɵɵgetInheritedFactory(PickerInlineComponent)))(t || PickerInlineComponent);
    };
  })();
  PickerInlineComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: PickerInlineComponent,
    selectors: [["ng-picker-inline"]],
    features: [i0.ɵɵInheritDefinitionFeature],
    decls: 1,
    vars: 1,
    consts: [[1, "ng-picker", 3, "id"]],
    template: function PickerInlineComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵelement(0, "div", 0);
      }
      if (rf & 2) {
        i0.ɵɵproperty("id", ctx.elementId);
      }
    },
    encapsulation: 2,
    changeDetection: 0
  });
  return PickerInlineComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FilestackTransformPipe = /*#__PURE__*/(() => {
  class FilestackTransformPipe {
    constructor(filestackService) {
      this.filestackService = filestackService;
    }
    transform(value, transformOptions) {
      return this.filestackService.transform(value, transformOptions);
    }
  }
  FilestackTransformPipe.ɵfac = function FilestackTransformPipe_Factory(t) {
    return new (t || FilestackTransformPipe)(i0.ɵɵdirectiveInject(FilestackService, 16));
  };
  FilestackTransformPipe.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "filestackTransform",
    type: FilestackTransformPipe,
    pure: true
  });
  return FilestackTransformPipe;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let FilestackModule = /*#__PURE__*/(() => {
  class FilestackModule {
    static forRoot(config) {
      return {
        ngModule: FilestackModule,
        providers: [FilestackService, {
          provide: 'config',
          useValue: config
        }]
      };
    }
  }
  FilestackModule.ɵfac = function FilestackModule_Factory(t) {
    return new (t || FilestackModule)();
  };
  FilestackModule.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: FilestackModule
  });
  FilestackModule.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    providers: [FilestackService],
    imports: [CommonModule]
  });
  return FilestackModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of filestack-angular
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FilestackModule, FilestackService, FilestackTransformPipe, PickerDropPaneComponent, PickerInlineComponent, PickerOverlayComponent };
